"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_table_1 = require("react-table");
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const camelCase_1 = __importDefault(require("lodash/camelCase"));
const upperFirst_1 = __importDefault(require("lodash/upperFirst"));
const useRowSelection_1 = __importDefault(require("./useRowSelection"));
/**
 * A hook that creates an instance of a table that can be fed to a renderer.
 * @template T
 * @param {TableOptions<T>} options - the basic configuration required to build an instance of the table
 * @returns {TableInstance<T>} An instance of a table
 */
const useTable = ({ columns, data, defaultColumn, onFetchData, getHiddenTableColumns, sortProps = {}, paginatorProps = {}, columnToggleProps = {}, filterProps = {}, searchProps = {}, rowSelectionProps, getRowId, stateEventHandlers }) => {
    // Sorting
    const sortingDisabled = typeof sortProps === 'boolean' && !sortProps;
    const { sortBy = [] } = sortingDisabled ? {} : sortProps;
    const manualSortBy = onFetchData != null;
    // Expandable Rows
    const expandableRowsDisabled = !data.some((row) => Array.isArray(row === null || row === void 0 ? void 0 : row.subRows));
    // Pagination
    const paginationDisabled = typeof paginatorProps === 'boolean' && !paginatorProps;
    const { pageCount = 0, currentPage: pageIndex = 0, pageSize = paginationDisabled ? undefined : 10, showOutOf = false, totalCount = 0 } = paginationDisabled ? {} : paginatorProps;
    const manualPagination = pageCount != null && onFetchData != null;
    // Filtering
    const filtersDisabled = typeof filterProps === 'boolean' && !filterProps;
    const { filters = [], filterTypes = {} } = filtersDisabled
        ? {}
        : filterProps;
    const manualFilters = onFetchData != null;
    // Search
    const searchDisabled = typeof searchProps === 'boolean' && !searchProps;
    const { searchBy = undefined, filterTypes: searchFilterTypes = {} } = searchDisabled ? {} : searchProps;
    const manualSearch = onFetchData != null;
    const columnToggleDisabled = typeof columnToggleProps === 'boolean' && !columnToggleProps;
    const { hiddenColumns = [] } = columnToggleDisabled
        ? {}
        : columnToggleProps;
    // Row Selection
    const rowSelectionDisabled = typeof rowSelectionProps === 'boolean' && !rowSelectionProps;
    const { onRowSelect = undefined, selectedRowIds = {}, maxSelectedRows = undefined } = rowSelectionDisabled ? {} : rowSelectionProps;
    const manualRowSelect = onRowSelect != null;
    // State reducer
    const stateReducer = manualPagination
        ? (newState, action, prevState) => {
            const stateEventHandlerKey = `on${(0, upperFirst_1.default)((0, camelCase_1.default)(action.type))}`;
            const stateEventHandler = stateEventHandlers === null || stateEventHandlers === void 0 ? void 0 : stateEventHandlers[stateEventHandlerKey];
            stateEventHandler === null || stateEventHandler === void 0 ? void 0 : stateEventHandler(action);
            const { filters: prevFitlers, globalFilter: prevGlobalFilter, pageSize: prevPageSize } = prevState;
            const { filters: newFitlers, globalFilter: newGlobalFilter, pageSize: newPageSize } = newState;
            if (!(0, isEqual_1.default)(prevFitlers, newFitlers) ||
                !(0, isEqual_1.default)(prevGlobalFilter, newGlobalFilter) ||
                prevPageSize !== newPageSize) {
                return Object.assign(Object.assign({}, newState), { pageIndex: 0 });
            }
            return newState;
        }
        : (newState, action) => {
            const stateEventHandlerKey = `on${(0, upperFirst_1.default)((0, camelCase_1.default)(action.type))}`;
            const stateEventHandler = stateEventHandlers === null || stateEventHandlers === void 0 ? void 0 : stateEventHandlers[stateEventHandlerKey];
            stateEventHandler === null || stateEventHandler === void 0 ? void 0 : stateEventHandler(action);
            return newState;
        };
    return (0, react_table_1.useTable)({
        columns,
        data,
        defaultColumn,
        initialState: {
            sortBy,
            pageIndex,
            pageSize,
            filters,
            hiddenColumns: columnToggleDisabled
                ? []
                : getHiddenTableColumns(hiddenColumns),
            globalFilter: searchBy,
            selectedRowIds
        },
        getRowId,
        stateReducer,
        // Sorting
        autoResetSortBy: !manualSortBy,
        disableSortRemove: true,
        manualSortBy,
        // Pagination
        autoResetPage: !manualPagination,
        pageCount: manualPagination ? pageCount : -1,
        manualPagination,
        showOutOf,
        totalCount,
        // Filtering
        defaultCanFilter: false,
        filterTypes: filtersDisabled ? searchFilterTypes : filterTypes,
        autoResetFilters: !manualFilters,
        manualFilters,
        // Search
        manualGlobalFilter: manualSearch,
        // Row Selection
        autoResetSelectedRows: !manualRowSelect,
        maxSelectedRows
    }, 
    // Filtering
    ...(filtersDisabled ? [] : [react_table_1.useFilters]), 
    // Search
    ...(searchDisabled ? [] : [react_table_1.useGlobalFilter]), 
    // Sorting
    ...(sortingDisabled ? [] : [react_table_1.useSortBy]), 
    // Expandable Rows
    ...(expandableRowsDisabled ? [] : [react_table_1.useExpanded]), 
    // Pagination
    ...(paginationDisabled ? [] : [react_table_1.usePagination]), 
    // Row Selection
    ...(rowSelectionDisabled ? [] : [react_table_1.useRowSelect, useRowSelection_1.default]));
};
exports.default = useTable;
//# sourceMappingURL=useTable.js.map